import React, { useState, useEffect, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Table from "../../../components/Table/Table";
import { getTeams, deleteTeam } from "../../../services/olympiad/teamService";
import { getRegions } from "../../../services/olympiad/regionService";
import { getExamCenters } from "../../../services/olympiad/examCenterService";
import { toast } from "react-toastify";
import { Button } from "../../../components/forms/Buttons/Button";
import ResultsPageHeader from "./PageHeader";
import TeamFilters from "./Filters";
import TeamViewModal from "./TeamViewModal";
import { useTeamColumns } from "./columns";
import { UserContext } from "../../../utils/contexts/UserContext";
import TableWithServerOrdering from "../../../components/Table/TableWithServerOrdering";

export default function Results() {
  const [t] = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [count, setCount] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [viewingTeam, setViewingTeam] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  // Filters
  const [regionFilter, setRegionFilter] = useState(null);
  const [teamMemberSearchFilter, setTeamMemberSearchFilter] = useState("");
  const [teamNameSearchFilter, setTeamNameSearchFilter] = useState("");
  const [ordering, setOrdering] = useState("-total_results");
  const [isPassedFilter, setIsPassedFilter] = useState(null);
  const [levelFilter, setLevelFilter] = useState("1");

  const { user } = useContext(UserContext);

  const isExamCenterAdmin = user?.roles?.some(
    (role) => role?.code === "EXAM_CENTER_ADMIN"
  );

  // Options
  const [regions, setRegions] = useState([]);

  useEffect(() => {
    if (isExamCenterAdmin) {
      return;
    }
    getRegions({})
      .then((res) => {
        setRegions(
          (res.results || []).map((r) => ({ label: r.name, value: r.uuid }))
        );
      })
      .catch((err) => console.error("Error fetching regions:", err));
  }, [isExamCenterAdmin]);

  const fetchData = useCallback(
    (signal) => {
      setLoading(true);
      getTeams({
        region: regionFilter,
        team_member_search: teamMemberSearchFilter || undefined,
        name: teamNameSearchFilter || undefined,
        is_passed: isPassedFilter,
        level: levelFilter,
        page: page,
        page_size: pageSize,
        ordering: ordering,
        signal,
      })
        .then((res) => {
          setData(res.results || []);
          setCount(res.count || 0);
          setPageCount(Math.ceil((res.count || 0) / pageSize));
        })
        .catch((err) => {
          if (signal?.aborted) return;
          console.error("Error fetching teams:", err);
          toast.error(t("admin.error_fetching_data"));
        })
        .finally(() => {
          if (signal?.aborted) return;
          setLoading(false);
        });
    },
    [
      regionFilter,
      teamMemberSearchFilter,
      teamNameSearchFilter,
      isPassedFilter,
      levelFilter,
      ordering,
      page,
      pageSize,
      t,
    ]
  );

  useEffect(() => {
    setPage(1);
  }, [
    regionFilter,
    teamMemberSearchFilter,
    teamNameSearchFilter,
    isPassedFilter,
    levelFilter,
    ordering,
    pageSize,
  ]);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  const handleDelete = (team) => {
    if (toast.isActive(team.uuid)) return;
    toast(
      <div className="flex flex-col gap-2 pt-2">
        <p>{t("admin.confirm_delete_team", { team: team.name })}</p>
        <div className="flex justify-end gap-2">
          <Button
            text={t("buttons.delete")}
            color="error"
            onClick={() => {
              toast.dismiss(team.uuid);
              deleteTeam(team.uuid)
                .then(() => {
                  toast.success(t("admin.team_deleted"));
                  fetchData();
                })
                .catch((err) => {
                  toast.error(
                    err.response?.data?.detail || t("admin.error_deleting_team")
                  );
                });
            }}
          />
          <Button
            text={t("buttons.cancel")}
            color="secondary"
            onClick={() => toast.dismiss(team.uuid)}
          />
        </div>
      </div>,
      { hideProgressBar: true, toastId: team.uuid }
    );
  };

  const handleView = (team) => {
    setViewingTeam(team);
    setIsViewModalOpen(true);
  };

  const handleUpdate = (teamUuid, body) => {
    setData((prev) =>
      prev.map((team) => (team.uuid === teamUuid ? { ...team, ...body } : team))
    );
  };

  const columns = useTeamColumns(
    handleDelete,
    handleView,
    isExamCenterAdmin,
    handleUpdate,
    pageSize
  );

  return (
    <div className="p-6">
      <ResultsPageHeader
        isExamCenterAdmin={isExamCenterAdmin}
        filterParams={{
          region: regionFilter,
          team_member_search: teamMemberSearchFilter || undefined,
          name: teamNameSearchFilter || undefined,
          is_passed: isPassedFilter,
          level: levelFilter,
          ordering: ordering,
        }}
      />

      <TeamFilters
        isExamCenterAdmin={isExamCenterAdmin}
        regionFilter={regionFilter}
        setRegionFilter={setRegionFilter}
        isPassedFilter={isPassedFilter}
        setIsPassedFilter={setIsPassedFilter}
        levelFilter={levelFilter}
        setLevelFilter={setLevelFilter}
        teamMemberSearchFilter={teamMemberSearchFilter}
        setTeamMemberSearchFilter={setTeamMemberSearchFilter}
        teamNameSearchFilter={teamNameSearchFilter}
        setTeamNameSearchFilter={setTeamNameSearchFilter}
        regions={regions}
      />

      <TableWithServerOrdering
        columns={columns}
        data={data}
        pageSize={pageSize}
        setPageSize={setPageSize}
        loading={loading}
        count={count}
        pageNumber={page}
        pageCount={pageCount}
        nextPage={() => setPage((p) => Math.min(p + 1, pageCount))}
        previousPage={() => setPage((p) => Math.max(p - 1, 1))}
        gotoPage={(p) => setPage(p)}
        ordering={ordering}
        handleOrdering={(newOrdering) => {
          setOrdering((prev) => {
            if (newOrdering === ordering) {
              return `-${ordering}`;
            } else if (newOrdering === `-${ordering}`) {
              return ordering;
            } else {
              return newOrdering;
            }
          });
        }}
      />

      <TeamViewModal
        isOpen={isViewModalOpen}
        team={viewingTeam}
        onClose={() => {
          setIsViewModalOpen(false);
          setViewingTeam(null);
        }}
      />
    </div>
  );
}
